import { useCallback } from "react";
import { useDispatch } from "react-redux";

import {
  ActivityMenuItem,
  CalculationTestData,
  CustomTestData,
} from "Hub/pages/TaskDetails/types";
import { Methodology } from "types/Methodology";

import {
  ClassificationMapped,
  MissingDataCheckEntities,
  ParametersMappedData,
  TaskDetails,
  VarianceChecksList,
} from "API/Endpoints/Tasks/payloads";

import { AppDispatch } from "store/store";

import {
  initialize,
  reload,
  setBusyMessages,
  setClassifiedData,
  setCurrentTaskDetailsData,
  setCustomFileData,
  setEncodedCalculationTestData,
  setErrorData,
  setFilesUploadData,
  setIsMenuFocus,
  setIsTaskClaimedByCurrentUser,
  setMenuItems,
  setMethodologies,
  setMissingDataMethodologyTab,
  setMissingEntities,
  setParametersMappedData,
  setReportedEmissionsData,
  setSelectedMenuItemId,
  setSubmitEnabled,
  setTabVisibility,
  setVarianceChecksData,
} from "./taskDetailsStore";
import {
  ErrorProps,
  FilesUploadData,
  ReloadTrigger,
  Tab,
  TabVisibility,
} from "./types";
import { flattenMissingEntities } from "./utils";

export interface UseTaskDetailsSetters {
  initialize: () => void;
  reload: (trigger: ReloadTrigger | undefined) => void;
  setCurrentTaskDetailsData: (value: TaskDetails | null) => void;
  setSelectedMenuItemId: (id: string) => void;
  setMenuItems: (menuItems: ActivityMenuItem[]) => void;
  setMissingDataMethodologyTab: (tab: Tab) => void;
  setEncodedCalculationTestData: (value: CalculationTestData | null) => void;
  setCustomFileData: (value: CustomTestData | null) => void;
  setReportedEmissionsData: (value: CalculationTestData | null) => void;
  setIsTaskClaimedByCurrentUser: (isClaimed: boolean) => void;
  setFilesUploadData: (
    filesUploadData: FilesUploadData | null | undefined
  ) => void;
  setParametersMappedData: (
    parametersMappedData: ParametersMappedData | null | undefined
  ) => void;
  setClassifiedData: (classifiedData: ClassificationMapped | null) => void;
  setVarianceChecksData: (data: VarianceChecksList | null) => void;
  setSubmitEnabled: (enabled: boolean) => void;
  setErrorData: (errorData: ErrorProps | null) => void;
  setTabVisibility: (tabVisibility: TabVisibility) => void;
  setMethodologies: (methodologies: Methodology[]) => void;
  setIsMenuFocus: (isMenuFocus: boolean) => void;
  setMissingEntities: (missingDataEntities: MissingDataCheckEntities) => void;
  setBusyMessages: (busyMessages: string[]) => void;
}

export const useTaskDetailsStore = (): UseTaskDetailsSetters => {
  const dispatch = useDispatch<AppDispatch>();

  return {
    initialize: useCallback(() => dispatch(initialize()), [dispatch]),

    reload: useCallback(
      (reloadTrigger: ReloadTrigger | undefined) =>
        dispatch(reload(reloadTrigger)),
      [dispatch]
    ),

    setCurrentTaskDetailsData: useCallback(
      (value: TaskDetails | null) => dispatch(setCurrentTaskDetailsData(value)),
      [dispatch]
    ),

    setSelectedMenuItemId: useCallback(
      (id: string) => dispatch(setSelectedMenuItemId(id)),
      [dispatch]
    ),

    setMenuItems: useCallback(
      (menuItems: ActivityMenuItem[]) => dispatch(setMenuItems(menuItems)),
      [dispatch]
    ),

    setMissingDataMethodologyTab: useCallback(
      (tab: Tab) => dispatch(setMissingDataMethodologyTab(tab)),
      [dispatch]
    ),

    setEncodedCalculationTestData: useCallback(
      (value: CalculationTestData | null) =>
        dispatch(setEncodedCalculationTestData(value)),
      [dispatch]
    ),

    setCustomFileData: useCallback(
      (fileData: CustomTestData | null) =>
        dispatch(setCustomFileData(fileData)),
      [dispatch]
    ),

    setReportedEmissionsData: useCallback(
      (value: CalculationTestData | null) =>
        dispatch(setReportedEmissionsData(value)),
      [dispatch]
    ),

    setIsTaskClaimedByCurrentUser: useCallback(
      (isClaimed: boolean) =>
        dispatch(setIsTaskClaimedByCurrentUser(isClaimed)),
      [dispatch]
    ),

    setFilesUploadData: useCallback(
      (filesUploadData: FilesUploadData | null | undefined) =>
        dispatch(setFilesUploadData(filesUploadData ?? null)),
      [dispatch]
    ),

    setParametersMappedData: useCallback(
      (parametersMappedData: ParametersMappedData | null | undefined) =>
        dispatch(setParametersMappedData(parametersMappedData ?? null)),
      [dispatch]
    ),

    setClassifiedData: useCallback(
      (classifiedData: ClassificationMapped | null) =>
        dispatch(setClassifiedData(classifiedData)),
      [dispatch]
    ),

    setVarianceChecksData: useCallback(
      (data: VarianceChecksList | null) =>
        dispatch(setVarianceChecksData(data)),
      [dispatch]
    ),

    setSubmitEnabled: useCallback(
      (enabled: boolean) => dispatch(setSubmitEnabled(enabled)),
      [dispatch]
    ),

    setErrorData: useCallback(
      (errorData: ErrorProps | null) => dispatch(setErrorData(errorData)),
      [dispatch]
    ),

    setTabVisibility: useCallback(
      (tabVisibility: TabVisibility) =>
        dispatch(setTabVisibility(tabVisibility)),
      [dispatch]
    ),

    setMethodologies: useCallback(
      (methodologies: Methodology[]) =>
        dispatch(setMethodologies(methodologies)),
      [dispatch]
    ),

    setIsMenuFocus: useCallback(
      (isMenuFocus: boolean) => dispatch(setIsMenuFocus(isMenuFocus)),
      [dispatch]
    ),

    setMissingEntities: useCallback(
      (missingEntities: MissingDataCheckEntities) =>
        dispatch(setMissingEntities(flattenMissingEntities(missingEntities))),
      [dispatch]
    ),

    setBusyMessages: useCallback(
      (busyMessages: string[]) => dispatch(setBusyMessages(busyMessages)),
      [dispatch]
    ),
  };
};
